// onshape-selection.js — THE MAIN PANEL FOLLOWS THE ONSHAPE SELECTION (ON-42).
//
// Why this exists: the panel's part tokens (`{$partId}`, `{$partNumber}`) are
// substituted ONCE, when Onshape loads the iframe. In the document registration
// there is no selection at load, so they arrive as literals and never change —
// Alex, 2026-09-24, screenshot: "part ID, etc.. does not update between
// selection". Re-invocation per click only happens in a "Selected part"
// registration, which blanks the whole panel when nothing is selected.
//
// Onshape's element-right-panel messaging is the other route: after an
// `applicationInit` handshake it posts a SELECTION message "upon user selection
// interactions". Zero REST calls, zero quota — pure postMessage.
//
// ✅ ON-42 ANSWERED YES, LIVE, 2026-09-24. Onshape does not document the payload;
// Alex's first click in a part studio read it off the Live selection block:
//   {"selections":[{"selectionType":"BODY","selectionId":"JJD",
//     "workspaceMicroversionId":"…"}],"messageName":"SELECTION"}
// The kind is in `selectionType` (there is no entityType), and a BODY's
// selectionId is the part id. The assembly shape is still unmeasured. The parser
// stays DEFENSIVE and ADDITIVE: anything it does not recognise yields null, and
// a null leaves the panel exactly as it was — URL tokens, `#NA`.
//
// SECURITY: messages are accepted only from Onshape's origin (the `server` param
// Onshape appends to our URL, else any https://*.onshape.com), and the handshake
// is posted to that origin, never '*' when it is known.
import { useEffect, useState } from 'react';
import { trustedOrigin, originAllowed, selectionFromMessage } from './onshape-selection-message.js';

// useOnshapeSelection(ids, server) → { status, count, selection, at }
//   status     'outside'   not inside an iframe — nothing to listen to
//              'no-ids'    documentId/workspaceId/elementId missing: the handshake
//                          cannot complete, so no SELECTION will ever arrive
//              'listening' handshake sent, waiting / receiving
//   count      SELECTION messages received (0 after a click = handshake refused
//              or this context does not send them — a diagnostic in itself)
//   selection  the latest selectionFromMessage() result, or null
//   handshake  where applicationInit was posted ('https://cad.onshape.com', '*'),
//              or 'failed: <reason>', or null when it was never attempted
//   total      EVERY message the panel received, from any origin, of any kind
//   last       the latest of those: { name, origin, trusted, preview, at }
//
// ⚠ WHY `total` AND `last` COUNT WHAT THE PANEL THEN IGNORES (2026-09-24). The
// first live test failed with nothing on screen: the filter dropped every message
// it could not read, so "Onshape sends nothing", "Onshape sends it from another
// origin" and "Onshape sends another shape" all looked identical — an unchanged
// #NA. Counting BEFORE filtering is what tells those three apart in one click.
// The preview is rendered as TEXT only; nothing untrusted is acted on.
export function useOnshapeSelection({ documentId, workspaceId, elementId }, server) {
  const inFrame = typeof window !== 'undefined' && window.parent && window.parent !== window;
  const hasIds = !!(documentId && workspaceId && elementId);
  const [state, setState] = useState({ count: 0, selection: null, at: null, handshake: null, total: 0, last: null });

  useEffect(() => {
    if (!inFrame || !hasIds) return undefined;
    const target = trustedOrigin(server);
    const onMessage = (ev) => {
      const trusted = originAllowed(ev.origin, server);
      const name = typeof ev.data?.messageName === 'string' ? ev.data.messageName : '(no messageName)';
      let preview;
      try { preview = JSON.stringify(ev.data); } catch { preview = String(ev.data); }
      preview = String(preview ?? '').slice(0, 400);
      const last = { name, origin: String(ev.origin || '(none)'), trusted, preview, at: new Date() };
      const isSel = trusted && /^selection$/i.test(name);
      const selection = isSel ? selectionFromMessage(ev.data) : null;
      setState(s => ({
        ...s,
        total: s.total + 1,
        last,
        count: s.count + (isSel ? 1 : 0),
        selection: selection ?? s.selection,
        at: selection ? new Date() : s.at,
      }));
    };
    window.addEventListener('message', onMessage);
    let handshake;
    try {
      window.parent.postMessage({ messageName: 'applicationInit', documentId, workspaceId, elementId }, target || '*');
      handshake = target || '*';
    } catch (e) { handshake = `failed: ${e?.message || e}`; }
    setState(s => ({ ...s, handshake }));
    return () => window.removeEventListener('message', onMessage);
  }, [inFrame, hasIds, documentId, workspaceId, elementId, server]);

  return { status: !inFrame ? 'outside' : !hasIds ? 'no-ids' : 'listening', ...state };
}
